import {
  extend,
  concat,
  filter,
  findIndex,
  forEach,
  isArray,
  map,
  set
} from 'lodash'
import * as log from 'loglevel'
import localforage from 'localforage'
import moment from 'moment'
import Promise from 'bluebird'
import Vue from 'vue'

import cu from '../../lib/chunkUtil'
import util from '../../lib/util'

import {
  // UPDATE_SCORES,
  ADD_ANSWER,
  ADD_CHUNK,
  ADD_COMMENT,
  ADD_RECOMMENDATION,
  ADD_SUGGESTION,
  DELETE_CHUNK,
  EMPTY_SIMPLE_QUESTIONS,
  GET_CHILDREN,
  GET_PROBLEMS,
  GET_RECOMMENDATIONS,
  GET_SUGGESTIONS,
  REMOVE_ANSWER,
  SET_CHUNK,
  SET_CHUNK_CROWD_RATING,
  SET_CHUNK_DUEDATE,
  SET_CHUNK_LAST_EDITED,
  SET_CHUNK_STARTDATE,
  SET_CHUNK_STATE,
  SET_CURRENT_ANSWERS,
  SET_CURRENT_QUESTION_ROOT,
  SET_PROBLEM_LABEL,
  SET_REPORT_ANSWERS,
  SET_REPORT_STATUS,
  UPDATE_ARG_GRAPH,
  UPDATE_CHILDREN,
  UPDATE_SIMPLE_QUESTIONS,
  UPDATE_TEXT
} from 'stores/types/chunk'

const state = {
  answersPerPage: 10,
  chunkMap: {},
  chunks: {},
  commentsPerPage: 10,
  currentAnswers: [],
  currentChunkCollection: null,
  currentDiscussion: null,
  currentPage: null,
  currentPageId: null,
  currentQuestion: null,
  currentQuestionChunks: null,
  currentQuestionRoot: null,
  currentSuggestions: null,
  localdb: {},
  pageMap: {},
  pages: {},
  problemCreating: {
    selectedGroups: [],
    newProblemContent:
      '<h1>Put your problem title here</h1><p>Describe your problem here</p>'
  },
  problemLabel: null,
  question: null,
  questions: [],
  questionsPerPage: 30,
  reportAnswers: [],
  reportStatus: null,
  simpleQuestions: []
}

const getters = {
  answersPerPage: state => state.answersPerPage,
  chunkMap: state => state.chunkMap,
  chunks: state => state.chunks,
  commentsPerPage: state => state.commentsPerPage,
  currentAnswers: state => state.currentAnswers,
  currentChunkCollection: state => state.currentChunkCollection,
  currentDiscussion: state => state.currentDiscussion,
  currentPage: state => state.currentPage,
  currentPageId: state => state.currentPageId,
  currentQuestion: state => state.currentQuestion,
  currentQuestionChunks: state => state.currentQuestionChunks,
  currentQuestionRoot: state => state.currentQuestionRoot,
  currentQuestionId: function(state) {
    return (state.currentQuestion && state.currentQuestion[0].id) || null
  },
  currentSuggestions: state => state.currentSuggestions,
  isConfirmable: state => {
    if (state.currentQuestionRoot && state.currentUser) {
      return util.isAuthoredBy(state.currentQuestionRoot, state.currentUser)
    }
    return false
  },
  localChunkStore: state => state.localdb.chunkStore,
  localdb: state => state.localdb,
  pageMap: state => state.pageMap,
  pages: state => state.pages,
  problemCreating: state => state.problemCreating,
  problemLabel: state => state.problemLabel,
  question: state => state.question,
  questionIsFrozen: state =>
    state.question && state.question.ext && state.question.ext.frozen,
  questionIsPending: state =>
    state.question && state.question.state === 'pending',
  questions: state => state.questions,
  questionsPerPage: state => state.questionsPerPage,
  questionState: state => state.question && state.question.state,
  reportAnswers: state => state.reportAnswers,
  reportStatus: state => state.reportStatus,
  simpleQuestions: state => state.simpleQuestions
}

function getOptions(ctx) {
  return {
    headers: {
      Authorization: ctx.rootState.user.token
    }
  }
}

localforage.config({
  driver: [localforage.INDEXEDDB, localforage.WEBSQL, localforage.LOCALSTORAGE],
  name: 'swarm'
})

let chunkStore = localforage.createInstance({
  name: 'chunkstore'
})

state.localdb.chunkStore = chunkStore
window.chunkStore = chunkStore

const actions = {
  async addArgGraph(ctx, params) {
    let parent = params.parent
    let child = params.child
    let ckSelected = params.ckSelected

    if (!ckSelected) {
      let chunk = await ctx.dispatch(ADD_CHUNK, child)
      ctx.commit(UPDATE_CHILDREN, {
        parent: parent,
        child: chunk,
        ckSelected: ckSelected
      })
      return chunk
    }
  },

  async addAnswer(ctx, change) {
    let chunk = await ctx.dispatch(ADD_CHUNK, change.chunk)
    chunk = await ctx.dispatch('getChunk', chunk.id)
    ctx.commit(ADD_ANSWER, { chunk })
    return chunk
  },

  /**
   * Create a new chunk
   * @param  {object}  ctx
   * @param  {object}  chunk - new template chunk object
   * @return {object}        - new chunk object
   */
  async [ADD_CHUNK](ctx, chunk) {
    try {
      const options = {
        chunk,
        options: getOptions(ctx)
      }
      let res = await ctx.rootState.api.addChunk(options)
      ctx.commit(SET_CHUNK, res)
      return res
    } catch (err) {
      throw err
    }
  },

  async addComment(ctx, params) {
    let parent = params.parent
    let child = params.child

    let chunk2 = await ctx.dispatch(ADD_CHUNK, child)
    ctx.commit(ADD_COMMENT, {
      parent: parent,
      child: chunk2
    })
    return chunk2
  },

  /**
   * Intervally backup chunk content
   * @param  {object} ctx
   * @param  {string} params.id      - chunk division id
   * @param  {string} params.content - chunk content
   * @return {Promise}
   */
  async backupContent(ctx, params) {
    try {
      let options = getOptions(ctx)
      let now = await ctx.rootState.api.getServerTime({ options })
      await chunkStore.setItem(params.id, {
        lastEdited: now,
        content: params.content
      })
    } catch (err) {
      throw err
    }
  },

  /**
   * Remove chunk content backup by division id
   * @param  {[type]}  ctx    [description]
   * @param  {string}  params.id - chunk division id
   * @return {Promise}        [description]
   */
  async clearBackupContent(ctx, params) {
    try {
      await chunkStore.removeItem(params.id)
    } catch (err) {
      throw err
    }
  },

  /**
   * Delete chunk by id
   * @param  {object}  ctx
   * @param  {string}  chunk.id - chunk id
   * @return {Promise}
   */
  async deleteChunk(ctx, chunk) {
    try {
      let options = {
        chunk: {
          id: chunk.id,
          state: 'deleted',
          updateMulti: chunk.updateMulti || false
        },
        options: getOptions(ctx)
      }
      let res = await ctx.rootState.api.updateChunk(options)
      ctx.commit(DELETE_CHUNK, res)
    } catch (err) {
      throw err
    }
  },

  /**
   * Empty simple problem
   * @param  {object} ctx
   */
  emptySimpleQuestions(ctx) {
    ctx.commit(EMPTY_SIMPLE_QUESTIONS)
  },

  /**
   * Generate report
   * @param  {object}  ctx
   * @param  {object}  params.chunk
   * @return {Promise}
   */
  async generateReport(ctx, params) {
    const options = getOptions(ctx)
    const payload = {
      questionId: params.chunk.id
    }
    try {
      const response = await ctx.rootState.api.generateReport({
        options: options,
        payload: payload
      })
      return response
    } catch (err) {
      throw err
    }
  },

  async getArgGraph(ctx, params, saved) {
    let parent = params.parent
    let selectedGraph = params.selectedGraph
    let isSaved = params.isSaved
    let childrenIds = parent.childrenIds
    let relation = 'description'
    let chunkIds = (childrenIds && childrenIds[relation]) || []
    let chunkId = null
    if (selectedGraph) {
      chunkId = selectedGraph.getId()
    } else {
      if (!isSaved) {
        return Promise.resolve(null)
      } else if (isSaved) {
        chunkId = chunkIds[0]
      }
    }
    let chunk = await ctx.dispatch('getChunk', chunkId)
    return chunk
  },

  /**
   * Get responses in problem and update currentAnswers in vuex store
   * @param  {object}  ctx
   * @param  {string}  params.id         - problem id
   * @param  {boolean}  params.fetchAll  - if fetch all responses
   * @param  {number}  params.offset     - offset for responses query
   * @param  {number}  params.limit      - limit for responses query
   * @return {Promise}   return responses based on params
   */
  async getAnswers(ctx, params) {
    const { id } = params
    delete params.id
    params.limit = params.limit || ctx.state.answersPerPage
    params.offset =
      params.offset !== undefined
        ? params.offset
        : ctx.state.currentAnswers.length
    const { userPreferences } = ctx.rootGetters
    const options = cu.getResponsesParams(
      Object.assign({}, params, {
        options: getOptions(ctx),
        userPreferences
      })
    )

    const answers = await ctx.rootState.api.getAnswers({ id }, options)
    let currentAnswers = concat([], answers)
    if (options.params.offset !== undefined && options.params.offset !== 0) {
      currentAnswers = map(ctx.state.currentAnswers, answer => {
        const chunkId = answer.id
        if (chunkId in ctx.state.chunks) {
          return Object.assign({}, ctx.state.chunks[chunkId])
        }
        return Object.assign({}, answer)
      })
      currentAnswers = concat(currentAnswers, ...answers)
    }
    forEach(currentAnswers, chunk => {
      ctx.commit(SET_CHUNK, chunk)
    })
    // update currentAnswers in vuex store
    ctx.commit(SET_CURRENT_ANSWERS, {
      chunks: currentAnswers || []
    })
    return answers
  },

  /**
   * Get chunk object by id
   * @param  {object}  ctx
   * @param  {string}  id - chunk id
   * @return {object}     - chunk object
   */
  async getChunk(ctx, id) {
    try {
      const options = {
        id,
        options: getOptions(ctx)
      }
      let chunk = await ctx.rootState.api.getChunk(options)
      ctx.commit(SET_CHUNK, chunk)
      return chunk
    } catch (err) {
      throw err
    }
  },

  /**
   * Get comments for a response
   * @param  {object}  ctx
   * @param  {object}  params.chunk      - chunk object
   * @param  {boolean} [params.fetchAll] - check if fetch all comments
   * @return {array}                     - return comments
   */
  async getComments(ctx, params) {
    const { fetchAll, chunk } = params
    let options = getOptions(ctx)
    if (!fetchAll) {
      let offset = chunk.comments.length
      let limit = ctx.state.commentsPerPage
      options.params = {
        limit: limit,
        offset: offset,
        sort: 'createdAt.desc'
      }
    }
    try {
      const chunks = await ctx.rootState.api.getComments(chunk, options)
      forEach(chunks, chunk => {
        ctx.commit(SET_CHUNK, chunk)
      })
      return chunks
    } catch (err) {
      console.error(`getComments err: ${JSON.stringify(err)}`)
    }
  },

  /**
   * Get problem chunk by label (T&E only)
   * @param  {object}  ctx
   * @param  {string}  label - chunk label
   * @return {object}        - chunk object
   */
  async getProblemByLabel(ctx, label) {
    try {
      const options = {
        label,
        options: getOptions(ctx)
      }
      let chunk = await ctx.rootState.api.getProblemByLabel(options)
      return chunk
    } catch (err) {
      throw err
    }
  },

  /**
   * Get problem and related responses
   * @param  {object}   ctx
   * @param  {string}   params.id       - problem id
   * @param  {boolean}  params.fetchAll - check if fetch all responses
   * @return {Promise}
   */
  async getQandA(ctx, { id, fetchAll, offset }) {
    try {
      let chunk = await ctx.dispatch('getChunk', id)
      ctx.commit(SET_CURRENT_QUESTION_ROOT, chunk)
      let options = {
        id,
        fetchAll
      }
      if (offset !== undefined) {
        options.offset = offset
      }
      await ctx.dispatch('getAnswers', options)
    } catch (err) {
      throw err
    }
  },

  /**
   * Get problem by id
   * @param  {object}  ctx
   * @param  {string}  params.id - problem id
   * @return {Promise}
   */
  async getQuestion(ctx, params) {
    const { id } = params
    try {
      let chunk = await ctx.dispatch('getChunk', id)
      ctx.commit(SET_CURRENT_QUESTION_ROOT, chunk)
    } catch (err) {
      throw err
    }
  },

  /**
   * Get report problem and its report response, and set report status globally
   * @param  {object}  ctx
   * @param  {string}  params.id - problem id
   * @return {Promise}
   */
  async getReportQandA(ctx, params) {
    const REPORT_STATUS = ctx.getters.CONSTS.REPORT_STATUS
    try {
      set(window, '__SWARM__.REPORT_STATUS', '')
      let chunk = await ctx.dispatch('getChunk', params.id)
      ctx.commit(SET_CURRENT_QUESTION_ROOT, chunk)
      let options = getOptions(ctx)
      options.params = {
        limit: 1,
        offset: 0,
        type: 'claim',
        sort:
          'banded_rating.desc,quality.desc,rating_count.desc,lastEdited.desc'
        // sort: 'quality.desc,lastEdited.desc'
      }
      let answers = await ctx.rootState.api.getAnswers(params, options)
      const status = cu.setReportStatus(answers, REPORT_STATUS)
      ctx.commit(SET_REPORT_ANSWERS, {
        chunks: answers || []
      })
      ctx.commit(SET_REPORT_STATUS, status)
      return answers
    } catch (err) {
      throw err
    }
  },

  /**
   * Get chunk scores by chunk id
   * @param  {Object}  ctx
   * @param  {String}  params.id - chunk id
   * @return {Promise}
   */
  async getChunkScores(ctx, params) {
    const { id } = params
    const options = getOptions(ctx)
    let scores = await ctx.rootState.api.getScores({ id, options })
    return scores
  },

  /**
   * Get simple problems
   * @param  {object}  ctx    [description]
   * @param  {string}  params.state       - problem state
   * @param  {boolean}  params.showdeleted - check if return problem with 'deleted' === true
   * @return {Promise}
   */
  async getSimpleQuestions(ctx, params) {
    let { state, showDeleted } = params
    const options = getOptions(ctx)
    const limit = ctx.state.questionsPerPage
    const offset = filter(ctx.getters.simpleQuestions, { state: state }).length
    let chunks = await ctx.rootState.api.getSimpleQuestions({
      state,
      showDeleted,
      options,
      limit,
      offset
    })
    ctx.commit(UPDATE_SIMPLE_QUESTIONS, {
      chunks: chunks
    })
    return chunks
  },

  /**
   * Send parallel requests to api to create replicated problem for multiple groups
   * @param   {object}  ctx
   * @param   {object}  params
   * @param   {Array<object>} params.groups
   * @param   {object}  params.chunk
   * @returns {Promise}
   */
  async multipleGroupNewProblem(ctx, params) {
    const groups = params.groups
    const groupTag = new Date().getTime().toString()
    const chunk = Object.assign({}, params.chunk, { tags: [groupTag] })
    let promises = []

    /* Create problem for one group first, before creating the others to prevent multiple creation of the shared
     * group "tag".
     */
    const firstGroup = groups.pop()
    const firstProblem = Object.assign({}, chunk, {
      userGroupIds: [firstGroup.id]
    })
    let createProblemForFirstGroup = ctx.dispatch(ADD_CHUNK, firstProblem)

    try {
      await createProblemForFirstGroup

      forEach(groups, group => {
        const problem = Object.assign({}, chunk, { userGroupIds: [group.id] })
        promises.push(ctx.dispatch(ADD_CHUNK, problem))
      })

      await Promise.all(promises)
    } catch (err) {
      log.error('newQuestion', err, err.response)
      throw err
    }
  },

  async newQuestion(ctx, params) {
    let question = cu.createChunk({
      content:
        '<h1>Put your problem title here</h1><p>Describe your problem here</p>',
      type: 'question',
      userGroupIds: [params.userGroupId] || null,
      startDate: moment()
        .add(1, 'w')
        .valueOf(),
      dueDate: moment()
        .add(2, 'w')
        .valueOf()
    })

    let chunk = await ctx.dispatch(ADD_CHUNK, question)
    return chunk
  },

  async removeAnswer(ctx, change) {
    let chunk = change.chunk
    await ctx.dispatch('deleteChunk', chunk)
    ctx.commit(REMOVE_ANSWER, change)
  },

  /**
   * Set chunk crowd rating
   * @param {[type]} ctx
   * @param {object} params.chunk - chunk
   * @param {object} params.value
   */
  setChunkCrowdRating(ctx, params) {
    ctx.commit(SET_CHUNK_CROWD_RATING, params)
  },

  /**
   * Update chunk
   * @param  {object}  ctx   [description]
   * @param  {[type]}  chunk [description]
   * @return {Promise}       [description]
   */
  async updateChunk(ctx, chunk) {
    try {
      const options = {
        chunk,
        options: getOptions(ctx)
      }
      let res = await ctx.rootState.api.updateChunk(options)
      ctx.commit(SET_CHUNK, res)
      ctx.commit(SET_CHUNK_LAST_EDITED, {
        chunk,
        newChunk: res
      })
      const index = findIndex(
        ctx.getters.currentAnswers,
        answer => answer.id === res.id
      )
      if (index !== -1) {
        let currentAnswers = [...ctx.getters.currentAnswers]
        currentAnswers[index] = res
        ctx.commit(SET_CURRENT_ANSWERS, { chunks: currentAnswers })
      }
      return res
    } catch (err) {
      throw err
    }
  },

  /**
   * Update problem due date
   * @param  {object}  ctx
   * @param  {string}  chunk.id      - problem id
   * @param  {number}  chunk.dueDate - new problem due date
   * @return {Promise}
   */
  async updateChunkDueDate(ctx, chunk) {
    let res = await ctx.dispatch('updateChunk', chunk)
    ctx.commit(SET_CHUNK_DUEDATE, chunk)
    return res
  },

  /**
   * Update problem start date
   * @param  {object}  ctx
   * @param  {string}  chunk.id      - problem id
   * @param  {number}  chunk.startDate - new problem start date
   * @return {Promise}
   */
  async updateChunkStartDate(ctx, chunk) {
    let res = await ctx.dispatch('updateChunk', chunk)
    ctx.commit(SET_CHUNK_STARTDATE, chunk)
    return res
  },

  /**
   * Update chunk state
   * @param  {object}  ctx          - vuex store object
   * @param  {string}  chunk.id     - chunk id
   * @param  {string}  chunk.state  - chunk state
   * @return {Promise}
   */
  async updateChunkState(ctx, chunk) {
    await ctx.dispatch('updateChunk', chunk)
    ctx.commit(SET_CHUNK_STATE, chunk)
  },

  /**
   * Update problem state
   * @param  {object}  ctx
   * @param  {string}  chunk.id      - problem id
   * @param  {string}  chunk.state   - problem state
   * @param  {object}  chunk.ext     - problem ext
   * @param  {string}  chunk.deleted - problem deleted
   * @return {Promise}
   */
  async updateQuestionState(ctx, chunk) {
    try {
      let res = await ctx.dispatch('updateChunk', chunk)
      ctx.commit(SET_CURRENT_QUESTION_ROOT, res)
      Vue.set(res, 'state', res.state)
    } catch (err) {
      throw err
    }
  },

  /**
   * Update a response rating scores
   * @param  {object}  ctx
   * @param  {object}  chunk - chunk object
   * @param  {object}  value - rating object
   * @return {object}        - scores object
   */
  async updateScores(ctx, { chunk, value }) {
    let id = chunk.id
    // ctx.commit('UPDATE_SCORES', {id: id, value: value})
    let scores = await ctx.rootState.api.updateScores({
      id: id,
      value: value,
      options: getOptions(ctx)
    })
    return scores
  },

  /**
   * Call api to recalculate agg. scores for all report in a question
   * @param ctx {object} - Store context
   * @param params {object} - Parameter
   *   { chunk: { id }
   *     options: {} // contains Auth header
   *   }
   * @returns {Promise<*|Promise<*>>} - Promise
   */
  async updateAggScores(ctx, params) {
    return ctx.rootState.api.updateAggScores({
      chunk: params.chunk,
      options: getOptions(ctx)
    })
  },

  /**
   * Upload file
   * @param  {object}  ctx
   * @param  {object}  params.formData - the file needs to be uploaded
   * @return {Promise}
   */
  async uploadFile(ctx, params) {
    const { formData } = params
    let { headers } = getOptions(ctx)
    headers['Content-Type'] = 'multipart/form-data'
    const options = {
      payload: formData,
      options: { headers }
    }
    return ctx.rootState.api.uploadFile(options)
  },

  /**
   * Get simple problems
   * @param  {object}  ctx context
   * @param  {string}  params.state        - problem state
   * @param  {number}  params.offset       - offset
   * @param  {number}  params.limit        - limit
   * @param  {boolean} params.showdeleted  - check if return problem with 'deleted' === true
   * @param  {string}  params.userGroup    - user group code
   * @return {Promise}
   */
  async [GET_PROBLEMS](ctx, params) {
    let { state, showDeleted, offset, limit, userGroup } = params
    const options = getOptions(ctx)
    let chunks = await ctx.rootState.api.getSimpleQuestions({
      state,
      showDeleted,
      options,
      limit,
      offset,
      userGroup
    })
    return chunks
  },

  [GET_SUGGESTIONS](ctx, params) {
    let rootGetters = ctx.rootGetters
    let parent = params.parent

    return ctx.rootState.api
      .getSuggestions({
        parent: parent,
        options: rootGetters.authOptions
      })
      .then(function(suggestions) {
        ctx.commit(GET_SUGGESTIONS, {
          suggestions: suggestions
        })
        return suggestions
      })
  },

  [ADD_SUGGESTION](ctx, params) {
    let child = params.child
    let parent = params.parent

    return ctx.dispatch(ADD_CHUNK, child).then(function(chunk) {
      ctx.commit(ADD_SUGGESTION, {
        parent: parent,
        child: chunk
      })
      return chunk
    })
  },

  [GET_CHILDREN](ctx, params) {
    let rootGetters = ctx.rootGetters

    return ctx.rootState.api
      .getChildren(
        extend(params, {
          options: rootGetters.authOptions
        })
      )
      .then(function(children) {
        return children
      })
  },

  [GET_RECOMMENDATIONS](ctx, params) {
    return ctx.dispatch(GET_CHILDREN, params).then(function(recommendations) {
      return recommendations
    })
  },

  [ADD_RECOMMENDATION](ctx, params) {
    return ctx.dispatch(ADD_CHUNK, params.child).then(function(chunk) {
      return chunk
    })
  }
}

const mutations = {
  [SET_CHUNK_CROWD_RATING](state, change) {
    let chunk = change.chunk
    Vue.set(chunk, 'crowdRating', change.value)
    Vue.set(state.chunks[chunk.id], 'crowdRating', change.value)
  },

  [SET_CHUNK_LAST_EDITED](state, change) {
    Vue.set(change.chunk, 'lastEdited', change.newChunk.lastEdited)
  },

  [UPDATE_SIMPLE_QUESTIONS](state, change) {
    state.simpleQuestions = concat(state.simpleQuestions, change.chunks)
  },

  [EMPTY_SIMPLE_QUESTIONS](state, change) {
    state.simpleQuestions = []
  },

  [SET_CURRENT_QUESTION_ROOT](state, chunk) {
    state.currentQuestion = [chunk]
    state.currentQuestionRoot = chunk
    state.question = chunk
  },

  [SET_PROBLEM_LABEL](state, problemLabel) {
    state.problemLabel = problemLabel
  },

  [SET_CURRENT_ANSWERS](state, change) {
    let chunks = change.chunks
    forEach(chunks, function(chunk) {
      let scores = chunk.scores
      let crowd = scores.crowd
      if (!crowd) {
        return
      }
      let crowdRating = 0
      let crowdNum = 0
      forEach(crowd.quality, function(val, key) {
        crowdRating += parseInt(key) * val
        crowdNum += val
      })
      chunk.crowdRating = Math.round(crowdRating / crowdNum)
    })
    state.currentAnswers = chunks
  },

  [SET_REPORT_ANSWERS](state, change) {
    let chunks = change.chunks
    forEach(chunks, function(chunk) {
      let scores = chunk.scores
      let crowd = scores.crowd
      if (!crowd) {
        return
      }
      let crowdRating = 0
      let crowdNum = 0
      forEach(crowd.quality, function(val, key) {
        crowdRating += parseInt(key) * val
        crowdNum += val
      })
      chunk.crowdRating = Math.round(crowdRating / crowdNum)
    })
    state.reportAnswers = chunks
  },

  [ADD_COMMENT](state, change) {
    let parent = change.parent
    let child = change.child
    if (!parent.childrenIds) {
      parent.childrenIds = {}
    }
    let comments = parent.childrenIds.comment

    if (!comments || !isArray(comments)) {
      comments = []
    }
    comments.splice(0, 0, child.id)
    parent.childrenIds.comment = comments
    // state.currentDiscussion.splice(0, 0, child)
  },

  [DELETE_CHUNK](state, chunk) {
    const { id } = chunk
    const stat = 'deleted'
    const deleted = true
    if (id in state.chunks) {
      Vue.set(state.chunks[id], 'state', stat)
      Vue.set(state.chunks[id], 'deleted', deleted)
    }
    const answerIdx = findIndex(state.currentAnswers, { id })
    if (answerIdx > -1) {
      state.currentAnswers[answerIdx].state = stat
      state.currentAnswers[answerIdx].deleted = deleted
    }
  },

  [ADD_ANSWER](state, change) {
    let chunk = change.chunk
    state.currentAnswers.splice(0, 0, chunk)
  },

  [REMOVE_ANSWER](state, change) {
    let idx = change.idx
    state.currentAnswers.splice(idx, 1)
  },

  [SET_CHUNK_STATE](state, change) {
    let id = change.id
    let status = change.state
    if (id in state.chunks) {
      Vue.set(state.chunks[id], 'state', status)
    }
    const answerIdx = findIndex(state.currentAnswers, { id })
    if (answerIdx > -1) {
      state.currentAnswers[answerIdx].state = status
    }
  },

  [SET_CHUNK_DUEDATE](state, change) {
    let { id, dueDate } = change
    if (id in state.chunks) {
      Vue.set(state.chunks[id], 'dueDate', dueDate)
    }
  },

  [SET_CHUNK_STARTDATE](state, change) {
    let { id, startDate } = change
    if (id in state.chunks) {
      Vue.set(state.chunks[id], 'startDate', startDate)
    }
  },

  [SET_CHUNK](state, chunk) {
    const id = chunk.id
    Vue.set(state.chunks, chunk.id, chunk)
    const answerIdx = findIndex(state.currentAnswers, { id })
    if (answerIdx > -1) {
      state.currentAnswers[answerIdx] = chunk
    }
  },
  [SET_REPORT_STATUS](state, status) {
    Vue.set(state, 'reportStatus', status)
  },

  // [UPDATE_SCORES] (state, change) {
  //   Vue.set(state.chunks[change.id].scores, 'mine', change.value)
  // },

  [UPDATE_TEXT](state, change) {
    const { id } = change
    if (change.chunk) {
      change.chunk.content = change.value
    }
    if (id in state.chunks) {
      state.chunks[id].content = change.value
    }

    const answerIdx = findIndex(state.currentAnswers, { id })
    if (answerIdx > -1) {
      state.currentAnswers[answerIdx].content = change.value
    }
  },
  [UPDATE_CHILDREN](state, change) {
    let parent = change.parent
    let child = change.child
    let relation = change.relation || change.child.parentRelation
    if (!parent.childrenIds) {
      parent.childrenIds = {}
    }
    let childrenIds = parent.childrenIds[relation]

    if (!childrenIds || !isArray(childrenIds)) {
      childrenIds = []
    }
    childrenIds.splice(0, 0, child.id)
    parent.childrenIds[relation] = childrenIds
  },
  [UPDATE_ARG_GRAPH](state, change) {},
  [GET_SUGGESTIONS](state, change) {
    state.currentSuggestions = change.suggestions
  },
  [ADD_SUGGESTION](state, change) {
    change.state = state
    change.stateKey = 'currentSuggestions'
  }
}

export default {
  getters,
  state,
  mutations,
  actions
}
